import type {
  StudioCheckoutPlan,
  StudioMockAuthState,
  StudioMockScenario,
} from './types';

const AUTH_STORAGE_KEY = 'rtts_studio_mock_auth';
const SCENARIO_STORAGE_KEY = 'rtts_studio_mock_scenario';
const ONBOARDED_STORAGE_KEY = 'rtts_studio_mock_onboarded';
const PROFILE_STORAGE_KEY = 'rtts_studio_mock_profile';

const VALID_AUTH: ReadonlySet<string> = new Set([
  'logged_out',
  'linked',
  'stale_link',
]);

const VALID_SCENARIO: ReadonlySet<string> = new Set([
  'free_ok',
  'free_capped',
  'paid_ok',
  'paid_capped',
  'admin_ok',
  'already_exists',
  'fail',
  'checkout_pending',
  'reels_empty',
  'vods_empty',
  'vods_unsupported_game',
]);

let memoryAuth: StudioMockAuthState = 'logged_out';
let memoryScenario: StudioMockScenario = 'free_ok';
/** Default false — new users see onboarding once. */
let memoryOnboarded = false;
let memoryDisplayName: string | null = null;
let memoryEmail: string | null = null;
let lastCheckoutPlan: StudioCheckoutPlan | null = null;
/** Remaining getEntitlement calls that still return free after mock checkout. */
let postCheckoutFreePollsRemaining = 0;

function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

/** Current mock auth/link state (in-memory + sessionStorage for reloads). */
export function getMockAuthState(): StudioMockAuthState {
  const stored = readStored(AUTH_STORAGE_KEY);
  if (stored && VALID_AUTH.has(stored)) {
    memoryAuth = stored as StudioMockAuthState;
  }
  return memoryAuth;
}

export function setMockAuthState(state: StudioMockAuthState): void {
  memoryAuth = state;
  writeStored(AUTH_STORAGE_KEY, state);
}

export function getMockScenario(): StudioMockScenario {
  const stored = readStored(SCENARIO_STORAGE_KEY);
  if (stored && VALID_SCENARIO.has(stored)) {
    memoryScenario = stored as StudioMockScenario;
  }
  return memoryScenario;
}

export function setMockScenario(scenario: StudioMockScenario): void {
  memoryScenario = scenario;
  writeStored(SCENARIO_STORAGE_KEY, scenario);
}

export function getMockOnboarded(): boolean {
  const stored = readStored(ONBOARDED_STORAGE_KEY);
  if (stored === 'true') memoryOnboarded = true;
  if (stored === 'false') memoryOnboarded = false;
  return memoryOnboarded;
}

export function setMockOnboarded(onboarded: boolean): void {
  memoryOnboarded = onboarded;
  writeStored(ONBOARDED_STORAGE_KEY, onboarded ? 'true' : 'false');
}

export function getMockProfileFields(): {
  display_name: string | null;
  email: string | null;
} {
  const stored = readStored(PROFILE_STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as {
        display_name?: string | null;
        email?: string | null;
      };
      if (typeof parsed.display_name === 'string' || parsed.display_name === null) {
        memoryDisplayName = parsed.display_name;
      }
      if (typeof parsed.email === 'string' || parsed.email === null) {
        memoryEmail = parsed.email;
      }
    } catch {
      // ignore
    }
  }
  return { display_name: memoryDisplayName, email: memoryEmail };
}

export function setMockProfileFields(fields: {
  display_name?: string | null;
  email?: string | null;
}): void {
  if ('display_name' in fields) {
    memoryDisplayName =
      fields.display_name === undefined ? memoryDisplayName : fields.display_name;
  }
  if ('email' in fields) {
    memoryEmail = fields.email === undefined ? memoryEmail : fields.email;
  }
  writeStored(
    PROFILE_STORAGE_KEY,
    JSON.stringify({ display_name: memoryDisplayName, email: memoryEmail })
  );
}

export function resetMockProfileState(): void {
  memoryOnboarded = false;
  memoryDisplayName = null;
  memoryEmail = null;
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(ONBOARDED_STORAGE_KEY);
      window.sessionStorage.removeItem(PROFILE_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export function getLastCheckoutPlan(): StudioCheckoutPlan | null {
  return lastCheckoutPlan;
}

export function setLastCheckoutPlan(plan: StudioCheckoutPlan | null): void {
  lastCheckoutPlan = plan;
}

/**
 * After mock checkout: keep entitlement free for `count` polls, then flip paid.
 * checkout_pending uses a high count so success route hits the fallback.
 */
export function beginMockCheckoutActivation(opts?: { pendingLong?: boolean }): void {
  if (opts?.pendingLong || getMockScenario() === 'checkout_pending') {
    postCheckoutFreePollsRemaining = process.env.NODE_ENV === 'test' ? 20 : 20;
  } else {
    postCheckoutFreePollsRemaining = process.env.NODE_ENV === 'test' ? 2 : 2;
  }
}

export function consumePostCheckoutFreePoll(): boolean {
  if (postCheckoutFreePollsRemaining <= 0) return false;
  postCheckoutFreePollsRemaining -= 1;
  return true;
}

export function getPostCheckoutFreePollsRemaining(): number {
  return postCheckoutFreePollsRemaining;
}

export function resetMockCheckoutState(): void {
  lastCheckoutPlan = null;
  postCheckoutFreePollsRemaining = 0;
}

/**
 * Dev-only: apply `?mock=` and `?scenario=` once per navigation.
 * Not a visible production control — query params only; inert in production.
 */
export function applyMockAuthQueryParam(search: string): StudioMockAuthState | null {
  if (typeof window === 'undefined') return null;
  if (process.env.NODE_ENV === 'production') return null;

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  let appliedAuth: StudioMockAuthState | null = null;

  const mockRaw = params.get('mock');
  if (mockRaw && VALID_AUTH.has(mockRaw)) {
    appliedAuth = mockRaw as StudioMockAuthState;
    setMockAuthState(appliedAuth);
  }

  const scenarioRaw = params.get('scenario');
  if (scenarioRaw && VALID_SCENARIO.has(scenarioRaw)) {
    setMockScenario(scenarioRaw as StudioMockScenario);
  }

  const onboardedRaw = params.get('onboarded');
  if (onboardedRaw === 'true' || onboardedRaw === '1') {
    setMockOnboarded(true);
  } else if (onboardedRaw === 'false' || onboardedRaw === '0') {
    setMockOnboarded(false);
  }

  return appliedAuth;
}

/** Simulate network latency so loading states are real. */
export function mockLatency(minMs = 400, maxMs = 800): Promise<void> {
  if (process.env.NODE_ENV === 'test') {
    return Promise.resolve();
  }
  const ms = minMs + Math.floor(Math.random() * (maxMs - minMs + 1));
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Stage advance interval for mock job progression (compressed in tests). */
export function mockJobStageMs(): number {
  if (process.env.NODE_ENV === 'test') return 0;
  return 900;
}
